import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatMessage as ApiChatMessage } from "@/api/generated/client/models";
import { ChatMessage } from "./ChatMessage";
import { TypingIndicator } from "./TypingIndicator";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

interface ChatMessagesListProps {
    messages: ApiChatMessage[];
    isLoading?: boolean;
    isSending?: boolean;
}

export function ChatMessagesList({ messages, isLoading = false, isSending = false }: ChatMessagesListProps) {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive or when typing indicator appears
    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length, isSending]);

    if (isLoading && messages.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <ScrollArea className="flex-1 w-full max-w-full overflow-x-hidden" ref={scrollAreaRef}>
            <div className="divide-y divide-border w-full overflow-x-hidden">
                {messages.map((message, index) => (
                    <ChatMessage
                        key={message.id}
                        message={message}
                        isLatest={index === messages.length - 1 && !isSending}
                    />
                ))}
                {isSending && <TypingIndicator />}
                <div ref={bottomRef} />
            </div>
        </ScrollArea>
    );
}
